import type { Phrase } from '../types';

/**
 * Usage-driven ordering for the phrase tiles — deliberately dampened, and deliberately manual.
 *
 * §5.4 is unusually firm about this: reorder **only** on an explicit `Neu sortieren`, never
 * automatically, never mid-conversation. A board that rearranges itself defeats motor memory, and for
 * this person motor memory may be worth more than optimal ranking. Somebody who has learned that
 * "Wasser" is second in the top row does not benefit from it being first.
 *
 * So this computes a *proposal*. A caregiver sees the new order and accepts it, exactly like the
 * calibration proposal in Phase 1. The app suggests; a human decides.
 *
 * Pure, so the ranking can be reasoned about without a database.
 */

/**
 * Recency half-life.
 *
 * Two weeks, not the twelve hours the prediction scorer uses. Prediction is about this conversation;
 * tile order is about this season of someone's life. A phrase that mattered daily last month should fade
 * gradually, not vanish because it went unused for an afternoon.
 */
const HALF_LIFE_DAYS = 14;

export interface OrderingInput {
  phrases: readonly Phrase[];
  nowMs: number;
}

export interface ProposedOrder {
  /** Phrases in the proposed order, with the order value they would be given. */
  proposal: Array<{ phrase: Phrase; order: number }>;
  /** False when the proposal matches what is already on screen — nothing to accept. */
  changed: boolean;
}

/**
 * A phrase's weight: how much it is used, decayed by how long ago.
 *
 * `useCount` alone would freeze the board around whatever was popular first — a phrase used forty times
 * two years ago would outrank one used ten times this week forever. The decay is what lets the board
 * follow the person's life.
 */
export function usageScore(phrase: Phrase, nowMs: number): number {
  if (phrase.useCount === 0) return 0;

  const lastUsed = phrase.lastUsedAt === undefined ? undefined : Date.parse(phrase.lastUsedAt);
  if (lastUsed === undefined || !Number.isFinite(lastUsed)) return phrase.useCount;

  const daysAgo = Math.max(0, (nowMs - lastUsed) / (24 * 60 * 60 * 1000));
  return phrase.useCount * Math.pow(0.5, daysAgo / HALF_LIFE_DAYS);
}

/**
 * Propose a new order within one category.
 *
 * **Pinning always wins.** A pinned phrase stays above every unpinned one regardless of use — pinning is
 * the caregiver's override, and an override that usage could outvote would not be one.
 */
export function proposeOrder(input: OrderingInput): ProposedOrder {
  const scored = input.phrases.map((phrase) => ({
    phrase,
    score: usageScore(phrase, input.nowMs),
  }));

  scored.sort((a, b) => {
    if (a.phrase.pinned !== b.phrase.pinned) return Number(b.phrase.pinned) - Number(a.phrase.pinned);
    if (b.score !== a.score) return b.score - a.score;
    // Stable tie-break on the existing order, so equal-scoring phrases do not shuffle between runs and
    // present a "change" that changes nothing meaningful.
    return a.phrase.order - b.phrase.order;
  });

  const proposal = scored.map((entry, index) => ({ phrase: entry.phrase, order: index }));

  const previousOrder = [...input.phrases].sort((a, b) => a.order - b.order).map((phrase) => phrase.id);
  const nextOrder = proposal.map((entry) => entry.phrase.id);
  const changed = previousOrder.some((id, index) => id !== nextOrder[index]);

  return { proposal, changed };
}
